import React, { useEffect, useState } from 'react';
import api from '../services/api';

interface Rule {
  id: number;
  name: string;
  description?: string;
  thresholdEntry: number;   // A북 전환 임계값 (음수, 예: -5.0)
  thresholdReturn: number;
  reEntryThreshold: number;  // 수동복귀 후 재진입 임계값 (예: -12.0)
  masterAccountId?: string;
  createdAt?: string;
  accountCount?: number;
}

// ── API ──────────────────────────────────────────────────────────
const rulesAPI = {
  getAll: () => api.get('/v1/rules'),
  create: (data: Omit<Rule, 'id'>) => api.post('/v1/rules', data),
  update: (id: number, data: Omit<Rule, 'id'>) => api.put(`/v1/rules/${id}`, data),
  delete: (id: number) => api.delete(`/v1/rules/${id}`),
};

// ── 모달 ─────────────────────────────────────────────────────────
const RuleModal: React.FC<{
  rule?: Rule | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ rule, onClose, onSuccess }) => {
  const isEdit = !!rule;
  const [name, setName] = useState(rule?.name || '');
  const [description, setDescription] = useState(rule?.description || '');
  const [thresholdEntry, setThresholdEntry] = useState(
    rule?.thresholdEntry?.toString() || '-5.00'
  );
  const [thresholdReturn, setThresholdReturn] = useState(
    rule?.thresholdReturn?.toString() || '0'
  )
  const [reEntryThreshold, setReEntryThreshold] = useState(
    rule?.reEntryThreshold?.toString() || '-10.00'
  );
  const [masterAccountId, setMasterAccountId] = useState(rule?.masterAccountId || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const entryVal = parseFloat(thresholdEntry);
  const returnVal = parseFloat(thresholdReturn);
  const reEntryVal = parseFloat(reEntryThreshold);

  const validate = () => {
    if (!name.trim()) return '룰 이름을 입력해주세요.';
    if (isNaN(entryVal) || entryVal >= 0) return '진입 임계값은 음수여야 합니다. (예: -5)';
    if (returnVal !== 0 && returnVal <= entryVal)
      return '복귀 임계값은 진입 임계값보다 커야 합니다. (예: 진입 -5, 복귀 -2)';
    return '';
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setLoading(true);
    setError('');
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        thresholdEntry: entryVal,
        thresholdReturn: returnVal,
        reEntryThreshold: reEntryVal,
        masterAccountId: masterAccountId.trim() || undefined,
      };
      if (isEdit) {
        await rulesAPI.update(rule!.id, payload);
      } else {
        await rulesAPI.create(payload);
      }
      onSuccess();
      onClose();
    } catch (e: any) {
      setError(e.response?.data?.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={modal.overlay} onClick={onClose}>
      <div style={modal.box} onClick={e => e.stopPropagation()}>
        {/* 헤더 */}
        <div style={modal.header}>
          <div>
            <div style={modal.title}>{isEdit ? '룰 수정' : '새 룰 만들기'}</div>
            <div style={modal.titleSub}>Drawdown 기반 A북 전환 조건</div>
          </div>
          <button style={modal.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* 에러 */}
        {error && <div style={modal.error}>{error}</div>}

        {/* 바디 */}
        <div style={modal.body}>
          {/* 룰 이름 */}
          <div style={modal.field}>
            <label style={modal.label}>룰 이름 <span style={{ color: '#ff6b6b' }}>*</span></label>
            <input
              style={modal.input}
              placeholder="예: 기본 룰, 공격적 트레이더용"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
            />
          </div>

          {/* 설명 */}
          <div style={modal.field}>
            <label style={modal.label}>설명 (선택)</label>
            <input
              style={modal.input}
              placeholder="이 룰에 대한 간단한 메모"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>

          {/* 마스터 계좌번호 */}
          <div style={modal.field}>
            <label style={modal.label}>마스터 계좌번호 (헤지 계좌)</label>
            <input
              style={modal.input}
              placeholder="예: 200001 (LP 연결 Netting 계좌)"
              value={masterAccountId}
              onChange={e => setMasterAccountId(e.target.value)}
            />
            <span style={modal.hint}>이 룰로 A북 전환 시 해당 계좌로 헤지 포지션 진입</span>
          </div>

          {/* 임계값 */}
          <div style={modal.row}>
            <div style={modal.field}>
              <label style={modal.label}>진입 임계값 (%)</label>
              <input
                style={modal.input}
                type="number"
                step="0.5"
                max="-0.1"
                value={thresholdEntry}
                onChange={e => setThresholdEntry(e.target.value)}
              />
              <span style={modal.hint}>이 값 이하 → A북 전환</span>
            </div>
            <div style={modal.field}>
              <label style={modal.label}>복귀 임계값 (%)</label>
              <input
                style={modal.input}
                type="number"
                step="0.5"
                value={thresholdReturn}
                onChange={e => setThresholdReturn(e.target.value)}
              />
              <span style={modal.hint}>0 입력 시 비활성 (포지션 청산 시 복귀)</span>
            </div>
            <div style={modal.field}>
              <label style={modal.label}>수동복귀 후 재진입 임계값 (%)</label>
              <input
                style={modal.input}
                type="number"
                step="0.1"
                value={reEntryThreshold}
                onChange={e => setReEntryThreshold(e.target.value)}
                placeholder="예: -12.00"
              />
              <span style={modal.hint}>수동 B북 복귀 후 이 값 이하일 때만 A북 재전환</span>
            </div>
          </div>

          {/* 미리보기 */}
          <div style={modal.preview}>
            <div style={modal.previewTitle}>📋 룰 미리보기</div>
            <div style={modal.previewRow}>
              <span style={modal.previewLabel}>A북 전환</span>
              <span style={{ color: '#ff6b6b', fontWeight: 600 }}>
                Drawdown {isNaN(entryVal) ? '?' : `${entryVal}%`} 이하
              </span>
            </div>
            <div style={modal.previewRow}>
              <span style={modal.previewLabel}>B북 복귀</span>
              <span style={{ color: '#00d2ff', fontWeight: 600 }}>
                {returnVal === 0 || isNaN(returnVal)
                  ? '포지션 전체 청산 시'
                  : `Drawdown ${returnVal}% 이상 회복 시`}
              </span>
            </div>
          </div>
        </div>

        {/* 푸터 */}
        <div style={modal.footer}>
          <button style={modal.cancelBtn} onClick={onClose}>취소</button>
          <button
            style={{ ...modal.submitBtn, opacity: loading ? 0.6 : 1 }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? '저장 중...' : isEdit ? '수정 완료' : '룰 생성'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── 삭제 확인 모달 ────────────────────────────────────────────────
const DeleteConfirmModal: React.FC<{
  rule: Rule;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}> = ({ rule, onClose, onConfirm, loading }) => (
  <div style={modal.overlay} onClick={onClose}>
    <div style={{ ...modal.box, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
      <div style={modal.header}>
        <div style={modal.title}>룰 삭제</div>
        <button style={modal.closeBtn} onClick={onClose}>✕</button>
      </div>
      <div style={{ padding: '24px 28px' }}>
        <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', lineHeight: 1.6 }}>
          <span style={{ color: '#fff', fontWeight: 600 }}>"{rule.name}"</span> 룰을 삭제하시겠습니까?
          {rule.accountCount ? (
            <span style={{ display: 'block', marginTop: '8px', color: '#ffa940' }}>
              ⚠️ 이 룰을 사용 중인 계좌가 {rule.accountCount}개 있습니다.
            </span>
          ) : null}
        </p>
      </div>
      <div style={modal.footer}>
        <button style={modal.cancelBtn} onClick={onClose}>취소</button>
        <button
          style={{ ...modal.submitBtn, background: 'linear-gradient(135deg, #ff4d4f, #cf1322)', opacity: loading ? 0.6 : 1 }}
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? '삭제 중...' : '삭제'}
        </button>
      </div>
    </div>
  </div>
);

// ── 메인 페이지 ───────────────────────────────────────────────────
const RulesPage: React.FC = () => {
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editRule, setEditRule] = useState<Rule | null>(null);
  const [deleteRule, setDeleteRule] = useState<Rule | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchRules = async () => {
    try {
      const res = await rulesAPI.getAll();
      setRules(res.data);
    } catch {
      setError('룰 목록을 불러오는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRules(); }, []);

  const handleDelete = async () => {
    if (!deleteRule) return;
    setDeleteLoading(true);
    try {
      await rulesAPI.delete(deleteRule.id);
      setDeleteRule(null);
      fetchRules();
    } catch (e: any) {
      setError(e.response?.data?.message || '삭제 중 오류가 발생했습니다.');
      setDeleteRule(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* 모달들 */}
      {(showModal || editRule) && (
        <RuleModal
          rule={editRule}
          onClose={() => { setShowModal(false); setEditRule(null); }}
          onSuccess={fetchRules}
        />
      )}
      {deleteRule && (
        <DeleteConfirmModal
          rule={deleteRule}
          onClose={() => setDeleteRule(null)}
          onConfirm={handleDelete}
          loading={deleteLoading}
        />
      )}

      {/* 헤더 */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>A북 전환 룰 관리</h1>
          <p style={styles.subtitle}>계좌에 적용할 Drawdown 기반 전환 조건을 설정합니다</p>
        </div>
        <button style={styles.createBtn} onClick={() => setShowModal(true)}>
          + 새 룰 만들기
        </button>
      </div>

      {/* 에러 */}
      {error && (
        <div style={styles.errorBanner}>
          {error}
          <button style={styles.errorClose} onClick={() => setError('')}>✕</button>
        </div>
      )}

      {/* 설명 배너 */}
      <div style={styles.infoBanner}>
        <span style={styles.infoIcon}>💡</span>
        <span style={styles.infoText}>
          룰을 먼저 만든 후, <strong>계좌 관리</strong>에서 B북 계좌 등록 시 룰을 선택하세요.
          각 계좌마다 다른 룰을 적용할 수 있습니다.
        </span>
      </div>

      {/* 룰 목록 */}
      {loading ? (
        <div style={styles.loading}>로딩 중...</div>
      ) : rules.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>📋</div>
          <div style={styles.emptyText}>등록된 룰이 없습니다</div>
          <div style={styles.emptySub}>새 룰 만들기 버튼을 눌러 첫 번째 룰을 생성하세요.</div>
          <button style={{ ...styles.createBtn, marginTop: '20px' }} onClick={() => setShowModal(true)}>
            + 새 룰 만들기
          </button>
        </div>
      ) : (
        <div style={styles.grid}>
          {rules.map(rule => (
            <div key={rule.id} style={styles.card}>
              {/* 카드 헤더 */}
              <div style={styles.cardHeader}>
                <div>
                  <div style={styles.ruleName}>{rule.name}</div>
                  {rule.description && (
                    <div style={styles.ruleDesc}>{rule.description}</div>
                  )}
                </div>
                <div style={styles.cardActions}>
                  <button style={styles.editBtn} onClick={() => setEditRule(rule)}>수정</button>
                  <button style={styles.deleteBtn} onClick={() => setDeleteRule(rule)}>삭제</button>
                </div>
              </div>

              {/* 조건 표시 */}
              <div style={styles.conditions}>
                <div style={styles.condition}>
                  <div style={styles.conditionLabel}>A북 전환</div>
                  <div style={styles.conditionArrow}>→</div>
                  <div style={styles.conditionValue}>
                    <span style={styles.conditionBadgeRed}>
                      전환: Drawdown {rule.thresholdEntry}% 이하
                    </span>
                  </div>
                </div>
                <div style={styles.conditionDivider} />
                <div style={styles.condition}>
                  <div style={styles.conditionLabel}>B북 복귀</div>
                  <div style={styles.conditionArrow}>→</div>
                  <div style={styles.conditionValue}>
                    {rule.thresholdReturn === 0 ? (
                      <span style={styles.conditionBadgeGray}>포지션 전체 청산 시</span>
                    ) : (
                      <span style={styles.conditionBadgeBlue}>
                        Drawdown {rule.thresholdReturn}% 이상 회복
                      </span>
                    )}
                  </div>
                </div>
                <div style={styles.conditionDivider} />
                <div style={styles.condition}>
                  <div style={styles.conditionLabel}>헤지 계좌</div>
                  <div style={styles.conditionArrow}>→</div>
                  <div style={styles.conditionValue}>
                    {rule.masterAccountId ? (
                      <span style={styles.conditionBadgePurple}>{rule.masterAccountId}</span>
                    ) : (
                      <span style={styles.conditionBadgeGray}>미설정</span>
                    )}
                  </div>
                </div>
              </div>

              {/* 사용 중인 계좌 수 */}
              <div style={styles.cardFooter}>
                <span style={styles.accountCount}>
                  {rule.accountCount != null
                    ? `📌 ${rule.accountCount}개 계좌에서 사용 중`
                    : '📌 사용 중인 계좌 없음'}
                </span>
                {rule.createdAt && (
                  <span style={styles.createdAt}>
                    {new Date(rule.createdAt).toLocaleDateString('ko-KR')} 생성
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── 스타일 ────────────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  container: { color: '#fff', maxWidth: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '28px', fontWeight: 700, color: '#fff', margin: '0 0 4px' },
  subtitle: { color: 'rgba(255,255,255,0.4)', fontSize: '13px', margin: 0 },
  createBtn: {
    padding: '10px 20px', background: 'linear-gradient(135deg, #667eea, #764ba2)',
    border: 'none', borderRadius: '10px', color: '#fff', fontSize: '14px',
    fontWeight: 600, cursor: 'pointer',
  },
  errorBanner: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', background: 'rgba(255,77,79,0.15)', border: '1px solid rgba(255,77,79,0.3)',
    borderRadius: '10px', color: '#ff6b6b', fontSize: '13px', marginBottom: '16px',
  },
  errorClose: { background: 'none', border: 'none', color: '#ff6b6b', cursor: 'pointer', fontSize: '16px' },
  infoBanner: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '12px 16px', background: 'rgba(102,126,234,0.08)', border: '1px solid rgba(102,126,234,0.2)',
    borderRadius: '10px', marginBottom: '24px',
  },
  infoIcon: { fontSize: '16px' },
  infoText: { color: 'rgba(255,255,255,0.6)', fontSize: '13px', lineHeight: 1.5 },
  loading: { padding: '60px', textAlign: 'center', color: 'rgba(255,255,255,0.4)' },
  empty: { textAlign: 'center', padding: '80px 40px' },
  emptyIcon: { fontSize: '56px', marginBottom: '16px' },
  emptyText: { color: '#fff', fontSize: '18px', fontWeight: 600, marginBottom: '8px' },
  emptySub: { color: 'rgba(255,255,255,0.4)', fontSize: '14px' },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '16px' },
  card: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '16px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px',
    transition: 'border-color 0.2s',
  },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' },
  ruleName: { fontSize: '16px', fontWeight: 700, color: '#fff', marginBottom: '4px' },
  ruleDesc: { fontSize: '12px', color: 'rgba(255,255,255,0.4)' },
  cardActions: { display: 'flex', gap: '8px', flexShrink: 0 },
  editBtn: {
    padding: '4px 12px', background: 'rgba(102,126,234,0.15)', border: '1px solid rgba(102,126,234,0.3)',
    borderRadius: '6px', color: '#667eea', fontSize: '12px', cursor: 'pointer',
  },
  deleteBtn: {
    padding: '4px 12px', background: 'rgba(255,77,79,0.1)', border: '1px solid rgba(255,77,79,0.2)',
    borderRadius: '6px', color: '#ff6b6b', fontSize: '12px', cursor: 'pointer',
  },

  conditions: {
    background: 'rgba(0,0,0,0.2)', borderRadius: '10px', padding: '14px 16px',
    display: 'flex', flexDirection: 'column', gap: '10px',
  },
  condition: { display: 'flex', alignItems: 'center', gap: '10px' },
  conditionLabel: { color: 'rgba(255,255,255,0.4)', fontSize: '12px', width: '64px', flexShrink: 0 },
  conditionArrow: { color: 'rgba(255,255,255,0.2)', fontSize: '12px' },
  conditionValue: { flex: 1 },
  conditionBadgeRed: {
    padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
    background: 'rgba(255,107,107,0.15)', color: '#ff6b6b',
  },
  conditionBadgeBlue: {
    padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
    background: 'rgba(0,210,255,0.15)', color: '#00d2ff',
  },
  conditionBadgeGray: {
    padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
    background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)',
  },
  conditionBadgePurple: {
    padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
    background: 'rgba(167,139,250,0.15)', color: '#a78bfa', fontFamily: 'monospace',
  },
  conditionDivider: { height: '1px', background: 'rgba(255,255,255,0.06)' },

  cardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  accountCount: { color: 'rgba(255,255,255,0.35)', fontSize: '12px' },
  createdAt: { color: 'rgba(255,255,255,0.25)', fontSize: '11px' },
};

const modal: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)',
  },
  box: {
    background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '20px', width: '520px', maxWidth: '95vw',
    boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '24px 28px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  title: { color: '#fff', fontSize: '20px', fontWeight: 700, margin: '0 0 2px' },
  titleSub: { color: 'rgba(255,255,255,0.35)', fontSize: '12px' },
  closeBtn: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '18px', cursor: 'pointer' },
  error: {
    margin: '16px 28px 0', padding: '12px 16px',
    background: 'rgba(255,107,107,0.15)', border: '1px solid rgba(255,107,107,0.3)',
    borderRadius: '10px', color: '#ff6b6b', fontSize: '13px',
  },
  body: { padding: '24px 28px' },
  field: { marginBottom: '16px', flex: 1 },
  label: { display: 'block', color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginBottom: '8px', fontWeight: 500 },
  hint: { color: 'rgba(255,255,255,0.35)', fontSize: '11px', marginTop: '4px' },
  input: {
    width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px', padding: '10px 14px', color: '#fff', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box',
  },
  row: { display: 'flex', gap: '16px' },
  preview: {
    background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '10px', padding: '14px 16px', marginTop: '4px',
  },
  previewTitle: { color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginBottom: '10px', fontWeight: 600 },
  previewRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' },
  previewLabel: { color: 'rgba(255,255,255,0.4)', fontSize: '13px' },
  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: '12px',
    padding: '20px 28px 24px', borderTop: '1px solid rgba(255,255,255,0.08)',
  },
  cancelBtn: {
    padding: '10px 20px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px', color: 'rgba(255,255,255,0.6)', fontSize: '14px', cursor: 'pointer',
  },
  submitBtn: {
    padding: '10px 24px', background: 'linear-gradient(135deg, #667eea, #764ba2)',
    border: 'none', borderRadius: '10px', color: '#fff', fontSize: '14px',
    fontWeight: 600, cursor: 'pointer',
  },
};

export default RulesPage;
